import { GraphEdge } from '../GraphEdge'

export function generateListEdges(templateField, templateFields, metadata) {
  const listGov = getListGovernance(templateField)

  if (!listGov) {
    console.warn(
      "[CLARAVINE] missing list_governance with governance_type = 'list'. ",
      templateField,
    )
    return []
  }

  const list = listGov.lists
  const type = list ? list.type : 'none'

  // calculate the edges based on the list type
  if (type === 'drill-down') {
    return generateDrilldownListEdges(templateField, templateFields, metadata)
  }
  if (type === 'dependent') {
    return generateDependentListEdges(templateField, templateFields, metadata)
  }
  if (type === 'drop-down') {
    return generateDropdownLists(templateField, templateFields, metadata)
  }

  // if the type doesn't match, no edges!
  return []
}

/**
 * Dropdown lists
 *
 * Dropdown lists are only 1-column wide.  Therefore columns tied to lists
 * of this type do not get affected by other columns.  They are always the
 * originators of action, not the receivers.
 *
 * @param templateField
 * @param templateFields
 * @param metadata
 * @returns {Array}
 */
function generateDropdownLists() {
  return []
}

/**
 * Drilldown Lists
 *
 * Each column in the drilldown list directly affects the column after it.
 * This can cause cascading effects, but it is still ordered according to the
 * list precedence.
 *
 * So... each column should point to the column that comes after it in precedence.
 *
 * Amazing ASCI art representation:
 * (numbers represent precedence)
 *
 * ---  ---  ---  ---
 * |1|  |2|  |3|  |4|
 * ---  ---  ---  ---
 *   \--/ \--/ \--/
 *
 * @param templateField
 * @param templateFields
 * @param metadata
 */
function generateDrilldownListEdges(templateField, templateFields, metadata) {
  const listGov = getListGovernance(templateField)

  if (!listGov) {
    return []
  }

  // get the template fields that are all tied to this same list
  const templateFieldsForList = getListTemplateFieldsForGovernance(listGov, metadata)

  // for each item, create an edge top map to it's predecessor
  // assumes that the templateFields for list are already sorted by precedence, asc
  const edges = templateFieldsForList.reduce((memo, templateField, idx) => {
    // there is nothing before the first item, so do nothing
    if (idx === 0) {
      return memo
    }

    // get the previous item
    const previousItem = templateFieldsForList[idx - 1]

    // create an edge from the previous to the current
    memo.push(new GraphEdge(previousItem.field_id, templateField.field_id))

    // continue the cyle!
    return memo
  }, [])

  // return the new edges
  return edges
}

/**
 * Dependent Lists
 *
 * The first column is the only one that can be updated.  All the rest just populate
 * based on the first columns value.
 *
 * So... the first column should point to all other columns.
 *
 * Amazing ASCI art representation:
 *
 * ---  ---  ---  ---
 * |1|  |2|  |3|  |4|
 * ---  ---  ---  ---
 *  \---/    /    /
 *  \-------/    /
 *  \-----------/
 *
 * @param templateField
 * @param templateFields
 * @param metadata
 */
function generateDependentListEdges(templateField, templateFields, metadata) {
  const listGov = getListGovernance(templateField)

  if (!listGov) {
    return []
  }

  // get the template fields that are all tied to this same list
  const templateFieldsForList = getListTemplateFieldsForGovernance(listGov, metadata)

  // get the first items
  const firstTemplateField = templateFieldsForList.shift()

  // for the remaining template fields, tie the first to the rest
  const edges = templateFieldsForList.map(toConvert => {
    return new GraphEdge(firstTemplateField.field_id, toConvert.field_id)
  })

  return edges
}

/**
 * Gets the list_governance for the template_field depending on where
 * it's supposed to come from (variable or not)
 *
 * @param templateField
 * @returns
 */
function getListGovernance(templateField) {
  const gov = templateField.field.segment_type === 'variable' ? templateField : templateField.field

  return gov.list_governance
}

/**
 * Common method for getting other template fields that are tied to this same
 * governance.
 *
 * We put it in a common method because of DRY and because I wanted to make
 * sure that we *always* are dealing with a copy, not the original dependency
 * array that might be reused elsewhere.
 *
 * @param listGov
 * @param metadata
 * @returns {...*|Array[]}
 */
function getListTemplateFieldsForGovernance(listGov, metadata) {
  // extract the columns from the list
  const listId = listGov.list_id
  const originalColumnsForList = metadata.columnsByListId[listId] || []

  // we need to copy it so that any slicing and dicing doesn't affect the original array
  return [...originalColumnsForList]
}
